// 判断是否处于浏览器环境，避免在非 DOM 场景访问 window/document 报错。
function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

// 站点级描述兜底：当页面未提供 description 时，避免出现空描述。
var DEFAULT_DESCRIPTION = '小小签到是一款专业免费的打卡签到小程序，支持活动签到、会议签到、班级签到、社群打卡和日常考勤等多场景使用。'

// 统一清洗文本：转字符串、压缩空白、去掉首尾空格。
function normalizeText(value) {
  if (!value) {
    return ''
  }
  return String(value).replace(/\s+/g, ' ').trim()
}

// 获取或创建指定属性的 meta 节点（如 name=description / property=og:title）。
function ensureMeta(attrName, attrValue) {
  if (!isBrowser()) {
    return null
  }
  // 同类 meta 只保留一个，后续路由切换时走“更新而非重复插入”。
  var selector = 'meta[' + attrName + '="' + attrValue + '"]'
  var node = document.head.querySelector(selector)
  if (!node) {
    node = document.createElement('meta')
    node.setAttribute(attrName, attrValue)
    document.head.appendChild(node)
  }
  return node
}

// 按 name 更新 meta；当 content 为空时删除该 meta，避免脏数据残留。
function setMetaByName(name, content) {
  if (!isBrowser()) {
    return
  }
  var value = normalizeText(content)
  var node = ensureMeta('name', name)
  if (!node) {
    return
  }
  if (!value) {
    node.parentNode && node.parentNode.removeChild(node)
    return
  }
  node.setAttribute('content', value)
}

// 按 property 更新 meta（主要用于 Open Graph）；空值时删除节点。
function setMetaByProperty(property, content) {
  if (!isBrowser()) {
    return
  }
  var value = normalizeText(content)
  var node = ensureMeta('property', property)
  if (!node) {
    return
  }
  if (!value) {
    node.parentNode && node.parentNode.removeChild(node)
    return
  }
  node.setAttribute('content', value)
}

// 设置 canonical 链接；传空值会移除 canonical。
export function setCanonical(url) {
  if (!isBrowser()) {
    return
  }
  var value = normalizeText(url)
  var node = document.head.querySelector('link[rel="canonical"]')
  if (!value) {
    if (node && node.parentNode) {
      node.parentNode.removeChild(node)
    }
    return
  }
  if (!node) {
    node = document.createElement('link')
    node.setAttribute('rel', 'canonical')
    document.head.appendChild(node)
  }
  node.setAttribute('href', value)
}

// 按固定 id 写入 JSON-LD 结构化数据；payload 为空时移除节点。
export function setJsonLd(id, payload) {
  if (!isBrowser()) {
    return
  }
  // 使用固定 id 复用节点，避免多次进入页面后注入重复的 JSON-LD。
  var node = document.getElementById(id)
  if (!payload) {
    if (node && node.parentNode) {
      node.parentNode.removeChild(node)
    }
    return
  }
  if (!node) {
    node = document.createElement('script')
    node.type = 'application/ld+json'
    node.id = id
    document.head.appendChild(node)
  }
  node.text = JSON.stringify(payload)
}

// 删除指定 id 的 JSON-LD 节点。
export function removeJsonLd(id) {
  setJsonLd(id, null)
}

// 一次性应用页面 SEO 信息：title、基础 meta、canonical、OG、Twitter。
export function applySeo(options) {
  if (!isBrowser()) {
    return
  }
  // 统一在一处同步 title/meta/canonical/OG，减少页面分散维护成本。
  var config = options || {}
  var title = normalizeText(config.title)
  // 页面未传 description 时自动兜底，避免 head 中 description 缺失。
  var description = normalizeText(config.description || DEFAULT_DESCRIPTION)
  var robots = normalizeText(config.robots)
  var canonical = normalizeText(config.canonical)
  var ogTitle = normalizeText(config.ogTitle || title)
  var ogDescription = normalizeText(config.ogDescription || description)
  var ogType = normalizeText(config.ogType || 'website')
  var ogUrl = normalizeText(config.ogUrl || canonical)
  // 社交分享图兜底到站点 favicon，保证至少有可用图片字段。
  var ogImage = normalizeText(config.ogImage || toAbsoluteUrl('/static/favicon.ico'))
  var twitterCard = normalizeText(config.twitterCard || 'summary_large_image')
  var twitterTitle = normalizeText(config.twitterTitle || ogTitle || title)
  var twitterDescription = normalizeText(config.twitterDescription || ogDescription || description)
  var twitterImage = normalizeText(config.twitterImage || ogImage)
  var twitterUrl = normalizeText(config.twitterUrl || ogUrl || canonical)

  if (title) {
    document.title = title
  }

  setMetaByName('description', description)
  setMetaByName('robots', robots)
  setMetaByName('keywords', normalizeText(config.keywords))
  setCanonical(canonical)

  setMetaByProperty('og:title', ogTitle)
  setMetaByProperty('og:description', ogDescription)
  setMetaByProperty('og:type', ogType)
  setMetaByProperty('og:url', ogUrl)
  setMetaByProperty('og:image', ogImage)
  setMetaByProperty('og:site_name', normalizeText(config.ogSiteName || '小小签到'))

  // 同步输出 twitter 卡片信息，避免仅有 Open Graph 时在部分平台丢失摘要。
  setMetaByName('twitter:card', twitterCard)
  setMetaByName('twitter:title', twitterTitle)
  setMetaByName('twitter:description', twitterDescription)
  setMetaByName('twitter:image', twitterImage)
  setMetaByName('twitter:url', twitterUrl)
}

// 将相对路径转为绝对 URL；已是绝对地址时原样返回。
export function toAbsoluteUrl(path) {
  if (!isBrowser()) {
    return ''
  }
  if (!path) {
    return window.location.href
  }
  if (/^https?:\/\//i.test(path)) {
    return path
  }
  var base = window.location.origin || ''
  return base + path
}

export default {
  applySeo: applySeo,
  setCanonical: setCanonical,
  setJsonLd: setJsonLd,
  removeJsonLd: removeJsonLd,
  toAbsoluteUrl: toAbsoluteUrl,
}



// WEBPACK FOOTER //
// ./src/utils/seo.js